import {
  type OpportunitiesSearchFilters,
  type OpportunitiesSearchOptions,
  type OpportunitiesSearchResponse,
  type OpportunitySearch,
  ProjectFilterDefault,
  initialOpportunityOptions,
  mapSearchResultToOpportunityArray,
  useFilters,
  useOpportunitiesSearch,
} from "@repo/api";
import { DEFAULT_LOCALE, getLanguageAndLocaleFromUrl } from "@repo/translation";
import { useTypesafeActions } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import {
  type ProjectFilterActions,
  projectFilterActions,
  projectFilterReducer,
} from "./filter/projectFilterReducer";
import { getFiltersFromSessionStorage } from "./filter/utils/sessionStorage";

interface SearchProject {
  result: OpportunitySearch[];
  totalSize: number | undefined;
  setSize: (
    size: number | ((_size: number) => number)
  ) => Promise<OpportunitiesSearchResponse[] | undefined>;
  size: number;
  options: OpportunitiesSearchOptions;
  setOptions: React.Dispatch<React.SetStateAction<OpportunitiesSearchOptions>>;
  actions: ProjectFilterActions;
  projectFilters: {
    filters: OpportunitiesSearchFilters;
    isLoaded: boolean;
  };
  isValidating: boolean;
  isLoading: boolean;
  brief?: string;
}

const SearchProjectContext = createContext<SearchProject>({} as SearchProject);

interface Props {
  pageSize?: number;
  children: ReactNode;
  refreshResult?: boolean;
}

export const SearchProjectProvider = ({
  children,
  pageSize,
  refreshResult = false,
}: Props) => {
  const locale = getLanguageAndLocaleFromUrl();
  const { language } = locale ?? DEFAULT_LOCALE;
  const filtersFromSessionStorage = getFiltersFromSessionStorage();
  const [projectFilters, actions] = useTypesafeActions<
    { filters: OpportunitiesSearchFilters; isLoaded: boolean },
    ProjectFilterActions
  >(
    projectFilterReducer,
    {
      filters: {
        ...ProjectFilterDefault,
        term: filtersFromSessionStorage.term,
      },
      isLoaded: false,
    },
    projectFilterActions
  );

  const [options, setOptions] = useState<OpportunitiesSearchOptions>({
    ...initialOpportunityOptions,
    pageSize: pageSize ?? initialOpportunityOptions.pageSize,
    sortKey: filtersFromSessionStorage.sortKey,
    sortOrder: filtersFromSessionStorage.sortOrder,
    refreshResult,
  });
  const { data, isValidating, isLoading, setSize, size } =
    useOpportunitiesSearch(
      !projectFilters.isLoaded ? null : projectFilters.filters,
      options
    );

  const result = mapSearchResultToOpportunityArray(data);
  const totalSize = data?.[0]?.totalElements;
  const brief = data?.[0]?.brief?.[language];
  return (
    <SearchProjectContext.Provider
      value={{
        result,
        totalSize,
        size,
        brief: brief,
        setSize,
        options,
        setOptions,
        actions,
        projectFilters,
        isValidating,
        isLoading,
      }}
    >
      {children}
    </SearchProjectContext.Provider>
  );
};

export const useSearchProject = () => useContext(SearchProjectContext);

export const useSearchProjectFilters = () => {
  const { actions } = useSearchProject();
  const { isLoaded, savedFilters } = useFilters();
  const [searchParams] = useSearchParams();

  const filtersFromSessionStorage = getFiltersFromSessionStorage();
  const filtersFromUrl = {
    term: decodeURIComponent(searchParams.get("searchTerm") ?? ""),
  };

  useEffect(() => {
    if (isLoaded) {
      const filters = isEmpty(savedFilters)
        ? ProjectFilterDefault
        : savedFilters;
      const filtersWithTerm = {
        ...filters,
        term: filtersFromUrl.term || filtersFromSessionStorage.term,
      };
      actions.setAllFilters(filtersWithTerm);
    }
  }, [
    isLoaded,
    savedFilters,
    actions,
    filtersFromSessionStorage.term,
    filtersFromUrl.term,
  ]);
};
